/**
 * Pixi renderer (v0.2).
 *
 * - Persistent sprite per bird (avoids GC churn and lets us interpolate motion).
 * - Smooth fly-in from off-screen → settle on perch (eased) → fly-out.
 * - Wing-flap by swapping between flight/takeoff textures during airborne phases.
 * - Idle bob + occasional feed-peck animation while perched.
 * - Coloured, sized food pellets that linger on the ground.
 */

import { Application, Container, Graphics, Sprite, Assets, Texture } from 'pixi.js';
import type { DayMeta } from '../data/dayMeta';
import type { Round, Bird, FoodPellet } from '../sim/round';
import { FEED_POSE_TICKS } from '../sim/round';
import { SIM_HZ } from '../sim/constants';

const POSES = ['idle', 'feed', 'flight', 'takeoff', 'land'] as const;
type Pose = (typeof POSES)[number];

/** Native facing of each pose as rendered in the source atlas. */
const NATIVE_FACING: Record<Pose, -1 | 1> = {
  idle: -1,
  feed: -1,
  flight: 1,
  takeoff: 1,
  land: 1,
};

interface BirdView {
  sprite: Sprite;
  /** Cached aspect ratio for stable scaling. */
  baseTex: Texture | null;
  /** Last facing direction (-1 left, +1 right). */
  facing: -1 | 1;
  /** Spawned-this-frame flag (for fade-in). */
  freshSpawn: boolean;
}

const FOOD_STYLE: Record<string, { color: number; radius: number; outline: number }> = {
  millet:    { color: 0xe9d6a3, radius: 6, outline: 0x6b5630 },
  berries:   { color: 0xc6395c, radius: 7, outline: 0x4a1422 },
  sunflower: { color: 0x3a2a1a, radius: 7, outline: 0x141008 },
};

export class RoundRenderer {
  private readonly app: Application;
  private readonly bgLayer: Container;
  private readonly foodLayer: Container;
  private readonly birdLayer: Container;
  private bg: Sprite | null = null;
  private birdPoseTextures = new Map<string, Map<Pose, Texture>>();
  private birdViews = new Map<Bird, BirdView>();

  constructor(app: Application) {
    this.app = app;
    this.bgLayer = new Container();
    this.foodLayer = new Container();
    this.birdLayer = new Container();
    this.app.stage.addChild(this.bgLayer, this.foodLayer, this.birdLayer);
  }

  async loadDay(meta: DayMeta, bundleUrl: string): Promise<void> {
    this.bgLayer.removeChildren();
    this.birdPoseTextures.clear();
    this.birdViews.clear();
    this.birdLayer.removeChildren();
    this.foodLayer.removeChildren();
    try {
      const tex: Texture = await Assets.load(`${bundleUrl}background.png`);
      this.bg = new Sprite(tex);
      this.fitBackground();
      this.bgLayer.addChild(this.bg);
    } catch {
      const g = new Graphics();
      g.rect(0, 0, this.app.renderer.width, this.app.renderer.height).fill({ color: 0x1a1f1a });
      this.bgLayer.addChild(g);
    }
    await this.loadBirdPoseTextures(meta, bundleUrl);
  }

  private async loadBirdPoseTextures(meta: DayMeta, bundleUrl: string): Promise<void> {
    for (const sp of meta.species) {
      const poseMap = new Map<Pose, Texture>();
      for (const pose of POSES) {
        const path = `${bundleUrl}birds/${sp.species_id}/${pose}.png`;
        try {
          const tex: Texture = await Assets.load(path);
          poseMap.set(pose, tex);
        } catch {
          /* missing pose is acceptable */
        }
      }
      if (poseMap.size > 0) this.birdPoseTextures.set(sp.species_id, poseMap);
    }
  }

  private fitBackground(): void {
    if (!this.bg) return;
    const { width, height } = this.app.renderer;
    const tex = this.bg.texture;
    const scale = Math.max(width / tex.width, height / tex.height);
    this.bg.scale.set(scale);
    this.bg.x = (width - tex.width * scale) / 2;
    this.bg.y = (height - tex.height * scale) / 2;
  }

  private textureFor(speciesId: string, pose: Pose): Texture | null {
    const map = this.birdPoseTextures.get(speciesId);
    if (!map) return null;
    return (
      map.get(pose) ??
      map.get('idle') ??
      map.get('flight') ??
      map.get('land') ??
      null
    );
  }

  /** Sync visual state from the simulation. */
  render(round: Round): void {
    this.renderFood(round);
    this.renderBirds(round);
  }

  private renderFood(round: Round): void {
    this.foodLayer.removeChildren();
    for (const f of round.state.food) {
      if (f.consumed) continue;
      const style = FOOD_STYLE[f.foodId] ?? FOOD_STYLE['millet']!;
      const g = new Graphics();
      // Shadow on ground.
      if (f.landed) {
        g.ellipse(f.x, f.groundY + 3, style.radius * 1.8, style.radius * 0.5)
          .fill({ color: 0x000000, alpha: 0.18 });
      }
      g.circle(f.x, f.y, style.radius)
        .fill({ color: style.color })
        .stroke({ color: style.outline, width: 1.5 });
      // Fade as ttl runs out (under 2s).
      const fadeTicks = 2 * SIM_HZ;
      if (f.ttl < fadeTicks) {
        g.alpha = Math.max(0.15, f.ttl / fadeTicks);
      }
      this.foodLayer.addChild(g);
    }
  }

  private renderBirds(round: Round): void {
    // 1. Prune sprites whose bird is gone.
    const alive = new Set<Bird>();
    for (const b of round.state.birds) if (!b.gone) alive.add(b);
    for (const [bird, view] of this.birdViews) {
      if (!alive.has(bird)) {
        view.sprite.destroy();
        this.birdViews.delete(bird);
      }
    }

    // 2. Update/create per bird.
    const tick = round.state.tick;
    for (const b of round.state.birds) {
      if (b.gone) continue;
      let view = this.birdViews.get(b);
      if (!view) {
        view = this.createBirdView(b);
        if (!view) continue;
        this.birdViews.set(b, view);
      }
      this.updateBirdView(b, view, tick);
    }
  }

  private createBirdView(b: Bird): BirdView | undefined {
    const tex = this.textureFor(b.speciesId, 'idle') ?? this.textureFor(b.speciesId, 'flight');
    if (!tex) return undefined;
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5, 0.85);
    // Source sprites are oriented left. Pick initial facing from the bird's
    // first goal so the very first frame already points the right way.
    const initialFacing: -1 | 1 = (b.targetX - b.x) >= 0 ? 1 : -1;
    const view: BirdView = { sprite, baseTex: tex, facing: initialFacing, freshSpawn: true };
    this.birdLayer.addChild(sprite);
    return view;
  }

  private updateBirdView(b: Bird, view: BirdView, tick: number): void {
    const speed = Math.hypot(b.vx, b.vy);
    const moving = speed > 25;            // px/sec threshold for "flying"
    const recentlyPecked = b.lastPeckAt >= 0 && tick - b.lastPeckAt < FEED_POSE_TICKS;

    // Pose selection driven by motion. While moving we stick to a single pose
    // (`flight`) and create the wing-flap with a small vertical scale wobble.
    // Swapping between `flight` and `takeoff` flips the sprite every frame
    // because those two textures face opposite directions in the source atlas.
    let pose: Pose;
    if (moving) {
      pose = 'flight';
    } else if (recentlyPecked) {
      pose = 'feed';
    } else {
      pose = 'idle';
    }

    // Render position: actual sim position + idle bob when perched.
    let renderX = b.x;
    let renderY = b.y;
    if (!moving && b.mode === 'perched') {
      const phase = (b.age / SIM_HZ) * 2 * Math.PI * 0.7;
      renderY += Math.sin(phase) * 1.6;
    }

    // Facing: derived from direction to current target when moving (stable),
    // otherwise keep previous. Using target instead of vx avoids flipping
    // when the bird eases into arrival and vx briefly oscillates near zero.
    if (b.mode === 'approaching' || b.mode === 'leaving') {
      const dx = b.targetX - b.x;
      if (Math.abs(dx) > 4) {
        view.facing = dx >= 0 ? 1 : -1;
      }
    }

    // Swap texture if pose changed.
    const tex = this.textureFor(b.speciesId, pose);
    if (tex && view.sprite.texture !== tex) {
      view.sprite.texture = tex;
    }
    // Scale to a sensible on-screen size based on long edge.
    const targetW = 96;
    const baseW = Math.max(1, view.sprite.texture.width);
    const baseS = targetW / baseW;
    // Native orientation of each pose in the source atlas:
    //   idle / feed / flight -> face LEFT  (native = -1)
    //   takeoff / land       -> face RIGHT (native = +1)
    // To render `view.facing`, flip only when desired != native.
    const native: -1 | 1 = NATIVE_FACING[pose];
    const flip = view.facing !== native;
    // Wing-flap while moving: subtle vertical squash at ~10 Hz.
    const flapY = moving ? 1 + 0.08 * Math.sin(tick * 0.6) : 1;
    view.sprite.scale.set(flip ? -baseS : baseS, baseS * flapY);

    view.sprite.x = renderX;
    view.sprite.y = renderY;

    // Fade-in on first frames.
    if (view.freshSpawn) {
      view.sprite.alpha = 0;
      view.freshSpawn = false;
    }
    if (view.sprite.alpha < 1) {
      view.sprite.alpha = Math.min(1, view.sprite.alpha + 0.05);
    }
  }

  resize(): void {
    this.fitBackground();
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}
function easeInCubic(t: number): number {
  return t * t * t;
}
